import {
  resetOCRAttempts,
  selectFamilyApplication,
  selectSelectedStudent,
  setSelectedStudent,
} from '@/app-storage/applications/familyApplicationSlice.ts'
import { AppDispatch } from '@/app-storage/store'
import AddStudentButton from '@/app/common/components/AddStudentButton/AddStudentButton'
import { InformationIcon } from '@/app/common/icons/general'
import { cn } from '@/app/common/utilities'
import FamilyWizardLayout from '@/app/pages/family_application/FamilyWizardLayout.tsx'
import { Button, buttonVariants } from '@/common/components/Button'
import { AppRoutePaths } from '@/routing/route_paths.ts'
import { selectProgramSettings } from '@/storage/program_settings/programSettingsSlice'
import { t } from 'i18next'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { Link, useNavigate } from 'react-router-dom'
import StudentStatusBadge from '../components/StudentStatusBadge'
import { LOG_TYPES, skipRenewalIncomeVerification } from '../constants'
import useFamilyApplicationRouting from '../routing/useFamilyApplicationRouting'
import {
  getCompletedStudents,
  getStudentNextStep,
  isGeneralStudentFirstTime,
  isRenewalStudentFirstTime,
} from '../step-checker/student_step_checker'
import { validateIncomeVerification } from '../step-checker/income-step-checker'
import { Student } from '@/apis/family_application/requests'
import { useEffect, useRef } from 'react'
import { LogUserActivity } from '@/apis/users/requests'

const { income_verification, review_application } =
  AppRoutePaths.family_application

export const AdditionalStudent = () => {
  const { t: translate } = useTranslation()
  const StoreDispatch = useDispatch<AppDispatch>()
  const FamilyApplicationState = useSelector(selectFamilyApplication)
  const programSettings = useSelector(selectProgramSettings)
  const selectedStudent = useSelector(selectSelectedStudent)
  const { onModuleEnd } = useFamilyApplicationRouting(FamilyApplicationState)

  const navigateTo = useNavigate()
  const applicantSkippedIncomeVerification =
    FamilyApplicationState.skip_income_verification == true
  const hasNoStudents = FamilyApplicationState.students.length === 0
  const completedStudents = getCompletedStudents(
    FamilyApplicationState.students,
    FamilyApplicationState
  )

  const eligibleStudentIds = completedStudents.filter((studentId) => {
    const studentHasUnresolvedFlag =
      FamilyApplicationState.flags.find(
        (flag) =>
          flag.student_id === studentId &&
          (flag.status === 'In Review' || flag.status === 'Rejected')
      ) != undefined

    return studentHasUnresolvedFlag == false
  })

  const applicantHasBadFlag = FamilyApplicationState.flags.some(
    (flag) =>
      flag.student_id === null &&
      (flag.status === 'Rejected' || flag.status === 'In Review')
  )
  const atLeastOneCompletedEligibleStudent =
    eligibleStudentIds.length > 0 && applicantHasBadFlag == false

  function onContinueClick() {
    StoreDispatch(resetOCRAttempts())
    const hasCompletedIncome =
      validateIncomeVerification(FamilyApplicationState).status === 'completed'

    if (hasCompletedIncome) {
      navigateTo(AppRoutePaths.family_application.review_application)

      return
    }

    const isSkippingIncome =
      skipRenewalIncomeVerification(
        programSettings,
        selectedStudent?.inception_status ?? 'New Applicant'
      ) || applicantSkippedIncomeVerification

    isSkippingIncome
      ? navigateTo(review_application)
      : navigateTo(onModuleEnd(income_verification.index))
  }

  function getRenewalStudentStatus(student: Student) {
    const status = student.application_status

    if (status === 'Ineligible') {
      return translate('ineligible')
    }

    if (status === 'Withdrawn') {
      return translate('withdrawn')
    }
    if (status === 'Eligible') {
      return translate('renewed')
    }


    return student.application_status
  }

  const hasLoggedPageView = useRef(false);
  useEffect(() => {
    if (!hasLoggedPageView.current) {
      LogUserActivity({
        log_type: LOG_TYPES.VIEW_PAGE,
        suffix_text: 'Add Student page',
      })
      hasLoggedPageView.current = true;
    }
  }, [])

  return (
    <FamilyWizardLayout>
      <FamilyWizardLayout.Header>
        <h1>{translate('additional_students')}</h1>
      </FamilyWizardLayout.Header>
      <FamilyWizardLayout.Content>
        <section className="mb-14">
          <h2 className="text-xl font-medium text-gray-900">
            {translate('question_text')}
          </h2>
          <div className="flex justify-end mt-8 w-full flex-col">
            <AddStudentButton />
            <p className="text-sm text-gray-600 mt-4">
              {translate('siblings_text')}
            </p>
          </div>
        </section>
        <section className="flex flex-col">
          <h2 className="font-medium text-gray-800 mt-3 mb-1 pb-2 border-b border-gray-300">
            {translate('sudents_added')}
          </h2>
          <ul role="list" className="divide-y divide-gray-300">
            {FamilyApplicationState?.students?.map((student, index) => {
              const isRenewal = student.inception_status === 'Renewal Student'
              const studentNextStep = getStudentNextStep(
                student,
                FamilyApplicationState
              )
              const hasOpenFlags = FamilyApplicationState.flags.some(
                (flag) =>
                  flag.student_id === student.id && flag.status == 'In Review'
              )
              const hasRejectedStudentFlags = FamilyApplicationState.flags.some(
                (flag) => {
                  return (
                    flag.student_id === student.id && flag.status == 'Rejected'
                  )
                }
              )

              const hasRejectedApplicantFlags =
                FamilyApplicationState.flags.some(
                  (flag) =>
                    flag.student_id === null && flag.status == 'Rejected'
                )

              const isWithdrawnStudent =
                student.application_status === 'Withdrawn'

              return (
                <li key={index} className="flex justify-between gap-x-6 py-5">
                  <div className="flex justify-between w-full min-w-0 gap-x-4">
                    <div
                      className={cn(`min-w-0 flex  w-full space-x-4`, {
                        'justify-between': isWithdrawnStudent,
                      })}
                    >
                      <h3 className="text-sm/6 font-semibold text-gray-900 flex items-center">
                        {student.first_name == null &&
                          student.last_name == null ? (
                          <span className="text-gray-500">
                            {translate('no_name')}
                          </span>
                        ) : (
                          `${student.first_name ?? ''} ${student.last_name ?? ''}`
                        )}
                      </h3>
                      <div>
                        <StudentStatusBadge
                          status={student.application_status}
                          subStatus={student.scholarship_application_sub_status}
                        />
                      </div>
                    </div>
                    {(function () {
                      if (student.is_external_student && student.inception_status === "Renewal Student") {
                        const status = getRenewalStudentStatus(student)


                        return (
                          <Button disabled>
                            {status}
                          </Button>
                        )
                      }

                      if (
                        hasRejectedStudentFlags ||
                        hasRejectedApplicantFlags
                      ) {
                        return null
                      }

                      if (student.application_status === 'Withdrawn') {
                        return null
                      }

                      if (isRenewal) {
                        if (isRenewalStudentFirstTime(studentNextStep)) {
                          return (
                            <Link
                              onClick={() => {
                                StoreDispatch(setSelectedStudent(student.id))
                              }}
                              to={
                                AppRoutePaths.family_application.student_details
                                  .enter_details
                              }
                              className={buttonVariants({ variant: 'primary' })}
                            >
                              {t('renew')}
                            </Link>
                          )
                        } else {
                          return <Button disabled={true}>{t('renewed')}</Button>
                        }
                      } else {
                        if (isGeneralStudentFirstTime(studentNextStep)) {
                          return (
                            <Link
                              onClick={() => {
                                StoreDispatch(setSelectedStudent(student.id))
                              }}
                              to={
                                AppRoutePaths.family_application.student_details
                                  .enter_details
                              }
                              className={buttonVariants({ variant: 'primary' })}
                            >
                              {t('apply')}
                            </Link>
                          )
                        }
                        if (studentNextStep.urls.length > 0) {
                          return (
                            <Link
                              onClick={() => {
                                StoreDispatch(setSelectedStudent(student.id))
                              }}
                              to={studentNextStep.urls[0]}
                              className={buttonVariants({ variant: 'primary' })}
                            >
                              {t('finish')}
                            </Link>
                          )
                        }
                      }

                      if (hasOpenFlags) {
                        return null
                      }

                      // Default: Render edit button
                      return (
                        <Link
                          to={
                            studentNextStep.urls.length > 0
                              ? studentNextStep.urls[0]
                              : AppRoutePaths.family_application.student_details
                                .enter_details
                          }
                          onClick={() => {
                            StoreDispatch(setSelectedStudent(student.id))
                          }}
                          className={buttonVariants({ variant: 'outline' })}
                        >
                          {t('edit')}
                        </Link>
                      )
                    })()}
                  </div>
                </li>
              )
            })}
          </ul>
        </section>
      </FamilyWizardLayout.Content>
      <FamilyWizardLayout.ActionBar>
        <div className="w-full">
          <Button
            disabled={hasNoStudents || !atLeastOneCompletedEligibleStudent}
            className="w-full rounded h-9 mt-5"
            onClick={onContinueClick}
          >
            {translate('continue')}
          </Button>
          {hasNoStudents && (
            <div className="text-sm text-center text-gray-600">
              <InformationIcon className="mr-2 mt-2" />
              You must renew or create at least one student to continue
            </div>
          )}
        </div>
      </FamilyWizardLayout.ActionBar>
    </FamilyWizardLayout>
  )
}
